<template>
    <v-dialog v-model="visible" class="w-75" scrollable @after-enter="onOpened">
        <v-card height="75vh">
            <v-card-title class="d-flex align-center flex-wrap ga-3 pa-4">
                <span>{{ $t(isConfig ? 'dialog.serverConfigFiles' : 'dialog.serverFiles') }}</span>

                <v-select v-model="customerSelector.selectedItem" :ref="el => customerSelector.component = el" :label="$t('field.customer')"
                variant="outlined" @update:model-value="onCustomerSelectorChange" density="compact"
                :items="customers" item-title="name" item-value="id"
                style="max-width: 240px" return-object hide-details/>

                <v-select v-if="!isConfig" v-model="pathSelector.selectedItem" :ref="el => pathSelector.component = el" :label="$t('field.favoritePaths')"
                variant="outlined" density="compact" hide-details clearable @update:model-value="onPathSelectorChange"
                :items="favoritePaths" :disabled="favoritePaths.length == 0" style="max-width: 260px" return-object />

                <v-spacer />

                <v-btn :prepend-icon="icons.mdiUpload" color="primary" icon-color="primary" variant="text" density="comfortable" :disabled="!canUpload" @click="triggerUpload" >{{ $t('action.upload') }}</v-btn>
                <v-btn :prepend-icon="icons.mdiDownload" color="primary" icon-color="primary" variant="text" density="comfortable" :disabled="!canDownload" @click="downloadServerFile" >{{ $t('action.download') }}</v-btn>
                <v-btn :prepend-icon="icons.mdiDelete" color="primary" icon-color="primary" variant="text" density="comfortable" :disabled="!canDelete" @click="deleteServerFile" >{{ $t('action.delete') }}</v-btn>

                <input ref="uploadInput" type="file" class="d-none" multiple accept=".png, .jpg, .jpeg, .bmp, .gif, .csv, .txt, .xls, .xlsx" @change="uploadFile" />
            </v-card-title>

            <v-divider />

            <AgGrid class="fill-height"  ref="uploadAgGridRef" :fetchUrl="fetchUrl"
            :columnDefs="columnDefs" :autoGroupColumnDef="autoGroupColumnDef"
            :groupDefaultExpanded="groupDefaultExpanded" :treeData="treeData" :gridParams="gridParams"
            :refresh="uploadRefresh" :rowSelection="rowSelection" :refreshCallback="expandOpenedGroup":domLayout="domLayout"/>

            <v-divider />

            <v-card-actions class="justify-end">
                <v-btn variant="text" color="error" @click="visible = false">{{ $t('action.cancel') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
    import { computed, inject, reactive, ref, useTemplateRef } from 'vue';
    import AgGrid from '@/components/AgGrid.vue';
    import { toast } from 'vue3-toastify';
    import { mdiDelete, mdiDownload, mdiUpload } from '@mdi/js';
    import { useControlStore } from '@/stores/controlStore';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';

    const props = defineProps({
        isConfig: {
            type: Boolean,
            default: false,
        },
        customers: {
            type: Array,
            default: () => [],
        },
    });

    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');

    const controlStore = useControlStore();
    const logStore = useLogStore();
    const userStore = useUserStore();

    const uploadInput = ref(null);

    const icons = {
        mdiDelete,
        mdiDownload,
        mdiUpload,
    };

    const visible = ref(false);

    const customerSelector = reactive({
        selectedItem: null,
        component: null,
    });
    const pathSelector = reactive({
        selectedItem: null,
        component: null,
    });

    const selectedCustomer = ref('');
    const selectedFavoritePath = ref('');

    const allFavoritePaths = ref([]);
    const favoritePaths = ref([]);

    const customerGroupKeysSelected = ref([]);
    const customerPathSelected = ref('');
    const selectedRowsCount = ref(0);

    const uploadGridRef = useTemplateRef('uploadAgGridRef');
    const fetchUrl = 'GetServerDirectoryFiles';
    const columnDefs = [
        {
            field: 'modifiedDate',
            type: 'date',
            headerName: translate('modifiedDate', 'column'),
            sort: 'desc',
        },
        {
            field: 'size',
            aggFunc: 'sum',
            headerName: translate('size', 'column'),
            type: 'size',
        },
        {
            field: 'favorite',
            headerName: translate('favorite', 'column'),
            type: 'favoriteAction',
        },
    ];
    const autoGroupColumnDef = {
        field: 'name',
        headerName: translate('name', 'column'),
        minWidth: 330,
        rowDrag: (params) => {
            return !params.node.group && typeof params.node.data != 'undefined' && params.node.data.path.last().indexOf('.') > -1;
        },
        cellRendererParams: {
            suppressCount: true,
            innerRenderer: 'fileSystemCellRenderer',
        },
    };
    const gridParams = reactive({
        gridClass: props.isConfig ? '.tree-config-grid' : '.tree-grid',
        token: userStore.getUser().token,
        customerReference: '',
        userId: userStore.getUser().id,
        isConfig: props.isConfig,
        isDefaultConfig: false,
    });

    const uploadRefresh = ref(false);
    const treeData = true;
    const groupDefaultExpanded = 1;
    const groupDisplayType = 'singleColumn';
    const rowSelection = {
        mode:'multiRow',
        checkboxLocation: 'autoGroupColumn',
        groupSelects: 'descendants'
    };
    const rowDragEntireRow = false;
    const domLayout = 'normal';

    const canDelete = computed(() => selectedRowsCount.value > 0 && (!props.isConfig || (props.isConfig && selectedCustomer.value != 'default')));

    const canDownload = computed(() => selectedRowsCount.value > 0);

    const canUpload = computed(() => customerPathSelected.value !== '' && customerPathSelected.value !== null);

    function open() {
        visible.value = true;
    }

    function onOpened() {
        if (!props.isConfig)
            getFavoritePaths();

        let activeCustomer = controlStore.getActiveCustomer() !== null ? controlStore.getActiveCustomer() : props.customers.filter(e => e.id != 0).first();

        if (activeCustomer != null && activeCustomer != selectedCustomer.value)
            customerSelector.component.$emit('update:modelValue', activeCustomer);
        else
            refreshGrid();
    }

    function onCustomerSelectorChange(){
        pathSelector.selectedItem = null;

        if (!props.isConfig) {
            gridParams.customerReference = customerSelector.selectedItem.reference;
            refreshGrid();

            controlStore.setActiveCustomer(customerSelector.selectedItem);
            controlStore.setActiveDistributor(null);

            favoritePaths.value = allFavoritePaths.value.filter(f => f.includes(customerSelector.selectedItem.reference));
        }
        else if (customerSelector.selectedItem.name != 'default') {
            gridParams.customerReference = customerSelector.selectedItem.reference;
            gridParams.isDefaultConfig = false;
            refreshGrid();

            controlStore.setActiveCustomer(customerSelector.selectedItem);
            controlStore.setActiveDistributor(null);
        }
        else {
            gridParams.customerReference = '';
            gridParams.isDefaultConfig = true;
            refreshGrid();

            controlStore.setActiveDistributor(null);
        }
    }

    function onPathSelectorChange(value){
        if (value == null || value == '') {
            customerGroupKeysSelected.value = [];
            customerPathSelected.value = '';
        }
        else {
            customerGroupKeysSelected.value = value.split('\\');
            customerPathSelected.value = '\\' + value;
            refreshGrid();
        }
    }

    function refreshGrid() {
        uploadGridRef.value.gridRef.api.setGridOption("loading", true);
        uploadRefresh.value = true;

        // setTimeout(() => {

        // }, 100);
    }

    function getFavoritePaths() {
        let data = {
            token: userStore.getUser().token,
            userId: userStore.getUser().id,
        };

        $.getData(urlWebAPI + 'GetFavoritePaths', data)
            .then((paths) => {
                allFavoritePaths.value = paths;
                favoritePaths.value = allFavoritePaths.value.filter(f => f.includes(customerSelector.selectedItem.reference));
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetFavoritePaths.');
            });
    }

    function expandOpenedGroup() {
        uploadGridRef.value.gridRef.api.setGridOption("loading", false);
        uploadRefresh.value = false;
        selectedRowsCount.value = 0;

        setTimeout(() => {
            let gridApi = uploadGridRef.value.gridRef.api;
            let path = customerGroupKeysSelected.value.join('\\');
            let pathOpened = false;

            gridApi.forEachNode((node) => {
                if (node.data && node.data.path.join('\\') == path) {
                    gridApi.setRowNodeExpanded(node, true, true);
                    pathOpened = true;
                }

                if (pathOpened)
                    return;
            });
        }, 500);
    }

    function onSelectionChanged(params) {
        selectedRowsCount.value = params.api.getSelectedRows().filter(r => r.path.last().indexOf('.') > -1).length;
    }

    function onRowClicked(params) {
        let lastPathPartHasNotExtension = typeof params.node.data != 'undefined' && params.node.data != null ? params.node.data.path.last().indexOf('.') == -1 : params.node.group;

        if (lastPathPartHasNotExtension) {
            customerGroupKeysSelected.value = params.node.getRoute();
            customerPathSelected.value = '\\' + customerGroupKeysSelected.value.join('\\') + '\\';
        }
        else {
            customerGroupKeysSelected.value = [];
            customerPathSelected.value = '';
        }
    }

    function onCellClicked(params) {
        if (typeof params.data != 'undefined' && typeof params.colDef.field != 'undefined' && params.colDef.field == 'favorite') {
            let notAFile = params.data.path.last().indexOf('.') == -1;

            if (notAFile)
                setPathAsFavorite(params);
        }
    }

    function onRowDragEnd(params) {
        moveServerFile(params);
    }

    function setPathAsFavorite(params) {
        let data = {
            token: userStore.getUser().token,
            path: params.node.data.path.join('\\'),
            isFavorite: !params.node.data.isFavorite,
            userId: userStore.getUser().id,
        };

        $.getData(urlWebAPI + 'SetPathAsFavorite', data)
            .then((paths) => {
                if (!props.isConfig) {
                    allFavoritePaths.value = paths;
                    favoritePaths.value = allFavoritePaths.value.filter(f => f.includes(customerSelector.selectedItem.reference));
                    refreshGrid();
                }
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing SetPathAsFavorite.');
            });
    }

    function moveServerFile(params) {
        if (typeof params.overNode != 'undefined' && typeof params.overNode.data != 'undefined') {
            let originalFilePaths = params.nodes.map(n => n.data.path.join('\\'));
            let destinationPath = params.overNode.data.path.filter(p => p.indexOf('.') == -1).join('\\');

            // SAME PATH - DO NOTHING
            if (params.node.data.path.filter(p => p.indexOf('.') == -1).join('\\') == params.overNode.data.path.filter(p => p.indexOf('.') == -1).join('\\'))
                return;

            let data = {
                fileName: params.node.data.path.last(),
                originalFilePaths: originalFilePaths,
                destinationPath: destinationPath,
                isConfig: props.isConfig,
            };

            $.getData(urlWebAPI + 'MoveServerFile', data)
                .then((success) => {
                    customerGroupKeysSelected.value = params.node.getRoute();
                    customerPathSelected.value = '\\' + customerGroupKeysSelected.value.join('\\');
                    refreshGrid();
                })
                .catch(error => {
                    logStore.log(error, 'error');
                    toast.error('Error during executing MoveServerFile.');
                });
        }

        // FOLDER NEED TO BE OPENED
        else
            toast.warn(translate('folderNeedToBeOpened', 'toast'));
    }

    function deleteServerFile() {
        let rows = uploadGridRef.value.gridRef.api.getSelectedRows();

        let data = {
            isConfig: props.isConfig,
            paths: rows.filter(r => r.path.last().indexOf('.') > -1).map(r => r.path.join('\\')),
        };

        $.getData(urlWebAPI + 'DeleteServerFile', data)
            .then((logs) => {
                refreshGrid();
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing DeleteServerFile.')
            });
    }

    function downloadServerFile() {
        let rows = uploadGridRef.value.gridRef.api.getSelectedRows();
        let paths = rows.filter(r => r.path.last().indexOf('.') > -1).map(r => r.path.join('\\'));
        let customerReference = customerSelector.selectedItem.reference ?? controlStore.getActiveCustomer()?.reference

        for (let i = 0; i < paths.length; i++) {
            let path = paths[i];
            let fileName = path.split('\\').last();

            $.downloadFile(null, path, fileName, urlWebAPI, customerReference, userStore.getUser().token, props.isConfig);
        }
    }

    function triggerUpload() {
        uploadInput.value.click();
    }

    function uploadFile(e) {
        if (e.target.files.length > 0) {
            for (let i = 0; i < e.target.files.length; i++) {
                let file = e.target.files[i];

                if (file.size > 5000000) {
                    toast.warn(translate('fileTooBig', 'toast'));
                    return false;
                }
            }

            $.uploadFiles(e.target.files, customerPathSelected.value, { urlWebAPI, translate }, props.isConfig, refreshGrid, props.isConfig);

            e.target.value = '';
        }
    }

    defineExpose({ open, onCellClicked, onSelectionChanged, onRowClicked, onRowDragEnd, groupDisplayType });
</script>
